import { useMedplum } from '@medplum/react-hooks';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useState } from 'react';

import {
  archiveTherapyGroup,
  createTherapyGroup,
  facilitatorReferencesFromForm,
  groupListItemFromResource,
  requestGroupAccessSync,
  requestAppointmentSeriesGenerate,
  searchTherapyGroups,
  syncGroupFacilitatorsToEnrolledMembers,
  updateTherapyGroup,
  type GroupListItem,
  type GroupSetupFormValues,
} from '@/lib/groupTherapy';
import { getGroupFacilitators } from '@unify/fhir-model/resources/group';
import type { Group } from '@medplum/fhirtypes';

function facilitatorKey(values: {
  primaryFacilitatorReference?: string;
  additionalFacilitatorReferences?: string[];
}): string {
  return facilitatorReferencesFromForm(values).slice().sort().join('|');
}

export function useGroupTherapyGroups(organizationId?: string) {
  const medplum = useMedplum();
  const [groups, setGroups] = useState<GroupListItem[]>([]);
  const [inactiveGroups, setInactiveGroups] = useState<GroupListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [groupResourcesById, setGroupResourcesById] = useState<Record<string, Group>>({});

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const all = await searchTherapyGroups(medplum, { organizationId });
      const active = all.filter((g) => g.active !== false);
      const inactive = all.filter((g) => g.active === false);
      setGroups(active.map(groupListItemFromResource));
      setInactiveGroups(inactive.map(groupListItemFromResource));
      const byId: Record<string, Group> = {};
      for (const g of all) {
        if (g.id) byId[g.id] = g;
      }
      setGroupResourcesById(byId);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [medplum, organizationId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const saveGroup = useCallback(
    async (values: GroupSetupFormValues, existingId?: string) => {
      const payload = values;

      const wasActive = existingId ? groupResourcesById[existingId]?.active !== false : false;
      const existingResource = existingId ? groupResourcesById[existingId] : undefined;
      const prevFacilitatorKey = existingResource
        ? getGroupFacilitators(existingResource)
            .map((r) => r.reference?.replace(/^Practitioner\//, '') ?? '')
            .filter(Boolean)
            .sort()
            .join('|')
        : '';
      const nextFacilitatorKey = facilitatorKey(payload);

      let saved: GroupListItem;
      if (existingId) {
        const existing = groupResourcesById[existingId];
        if (!existing) throw new Error('Group not found');
        saved = await updateTherapyGroup(medplum, payload, existing);
      } else {
        saved = await createTherapyGroup(medplum, payload);
      }

      // Active cohorts: generate on first activation; reconcile schedule edits on later saves.
      if (payload.active && saved.id) {
        const action = existingId && wasActive ? 'reconcile' : 'generate';
        try {
          const generateResult = await requestAppointmentSeriesGenerate(medplum, saved.id, action);
          const messages =
            generateResult.conflictMessages?.length
              ? generateResult.conflictMessages
              : (generateResult.conflicts ?? []).map((c) => {
                  if (typeof c === 'string') return c;
                  const label = c.actorType === 'room' ? 'Room' : 'Device';
                  return `${c.appointmentStart}: ${label} ${c.actorDisplay} is booked — scheduled without it`;
                });
          if (messages.length > 0) {
            const preview = messages.slice(0, 5).join('\n');
            const more =
              messages.length > 5 ? `\n…and ${messages.length - 5} more` : '';
            notifications.show({
              color: 'yellow',
              title: 'Sessions created with room/device conflicts',
              message:
                `Appointments were created. Conflicted rooms/devices were skipped so you can fix those sessions on the calendar.\n\n${preview}${more}`,
              autoClose: 12000,
            });
          }
        } catch (err) {
          const detail = err instanceof Error ? err.message : String(err);
          throw new Error(
            `Group saved, but appointment ${action} failed: ${detail}. Open the group and retry activation or schedule sync.`,
          );
        }
      }

      // Facilitator changes: sync CareTeams + access automatically (no manual Sync button).
      if (existingId && saved.id && prevFacilitatorKey !== nextFacilitatorKey) {
        try {
          const updated = await medplum.readResource('Group', existingId);
          await syncGroupFacilitatorsToEnrolledMembers({
            medplum,
            group: updated,
            facilitatorIds: facilitatorReferencesFromForm(payload),
            facilitatorDisplays: payload.facilitatorDisplays,
            organizationId: payload.organizationId,
          });
          await requestGroupAccessSync(medplum, { groupId: existingId, action: 'facilitators' });
        } catch (err) {
          const detail = err instanceof Error ? err.message : String(err);
          throw new Error(
            `Group saved, but facilitator sync failed: ${detail}. Member care teams may be out of date.`,
          );
        }
      }

      await reload();
      return saved;
    },
    [groupResourcesById, medplum, reload],
  );

  const archiveGroup = useCallback(
    async (groupId: string) => {
      const existing = groupResourcesById[groupId];
      if (!existing) throw new Error('Group not found');
      await archiveTherapyGroup(medplum, existing);
      await reload();
    },
    [groupResourcesById, medplum, reload],
  );

  return {
    groups,
    inactiveGroups,
    loading,
    error,
    reload,
    saveGroup,
    archiveGroup,
    getGroupResource: (id: string) => groupResourcesById[id],
  };
}
